import { supabase } from "@/integrations/supabase/client";
import type { PublicOrganizationArticle, PublicOrganizationProfile } from "@/lib/publicOrganizationProfile";

/**
 * The only place the anonymous organization RPCs are called from the app.
 *
 * Both functions are SECURITY DEFINER and anon-executable by design (migration
 * 20260913120000): they are the public read path for a published, non-seed
 * organization, and they return an explicit allowlist rather than table rows.
 * A hidden, unpublished, seed or unknown slug is NULL from every one of them,
 * with no distinguishing error — render not-found for null, nothing else.
 *
 * NEVER DETACH `supabase.rpc` FROM ITS CLIENT.
 *
 * `SupabaseClient.rpc` is `return this.rest.rpc(fn, args, options)` — it needs
 * its receiver. Hoisting it into a standalone `const rpc = supabase.rpc` drops
 * `this`, so the very first call throws `TypeError: Cannot read properties of
 * undefined (reading 'rest')` BEFORE any HTTP request is made. That is not a
 * theoretical hazard: it shipped in 94544a0f and broke every anonymous
 * `/organization/:slug` and `/organization/:slug/news/:contentId` page, which
 * rendered "not found" for real published organizations while the server, the
 * grant and the RPC were all working correctly. Nothing failed loudly, because
 * TanStack Query turns the throw into an ordinary error state.
 *
 * The build-time prerender never hit it — scripts/lib/public-organizations.mjs
 * calls PostgREST over plain HTTP — so the static HTML was right and only the
 * browser was wrong, which is what made it look like a server problem.
 *
 * `boundRpc` below therefore exists so the receiver is attached once, in one
 * named place, instead of depending on every future call site remembering it.
 *
 * The untyped boundary below exists because src/integrations/supabase/types.ts
 * is generated from the hosted schema and is regenerated only after a migration
 * has been applied to compaire-dev. When it is regenerated, drop the assertion
 * and let the generated signature type these calls — but keep the binding.
 */

type UntypedRpc = (
  fn: string,
  args?: Record<string, unknown>,
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;

/**
 * `supabase.rpc`, BOUND TO THE CLIENT. The `.bind(supabase)` is the fix — it is
 * not decoration, and removing it reintroduces the outage described above.
 *
 * Note the assertion is on the METHOD, not on the client: `src/lib/
 * task4MigrationGuards.test.ts` fails the suite for any `supabase as unknown
 * as ...` anywhere under `src/`, so asserting the whole object (which would let
 * an ordinary `client.rpc(...)` member call carry the receiver for free) is not
 * available here. Binding once is the remaining way to say it explicitly, and
 * this is the only line in the app that names `.rpc` on the public client.
 */
const boundRpc: UntypedRpc = (supabase.rpc as unknown as UntypedRpc).bind(supabase);

/** One RPC call, through the bound method. */
function callRpc(fn: string, args: Record<string, unknown>) {
  return boundRpc(fn, args);
}

export async function fetchPublicOrganizationProfile(slug: string): Promise<PublicOrganizationProfile | null> {
  if (!slug) return null;
  const { data, error } = await callRpc("get_public_organization_profile", { p_slug: slug });
  if (error) throw error;
  return (data as PublicOrganizationProfile | null) ?? null;
}

export async function fetchPublicOrganizationArticle(
  slug: string,
  contentId: string,
): Promise<PublicOrganizationArticle | null> {
  if (!slug || !contentId) return null;
  const { data, error } = await callRpc("get_public_organization_article", {
    p_slug: slug,
    p_content_id: contentId,
  });
  if (error) throw error;
  return (data as PublicOrganizationArticle | null) ?? null;
}

/**
 * The slugs of every public organization (owned profiles and, since #1039,
 * basic listings), from list_public_organizations(). The index carries no
 * name, city or type: callers read each organization's public profile for
 * those, through fetchPublicOrganizationProfile.
 */
export async function fetchPublicOrganizationSlugs(): Promise<string[]> {
  const { data, error } = await callRpc("list_public_organizations", {});
  if (error) throw error;
  if (!Array.isArray(data)) throw new Error("list_public_organizations returned a non-array");
  return data
    .map((row) => (row && typeof row === "object" ? (row as { slug?: unknown }).slug : null))
    .filter((slug): slug is string => typeof slug === "string" && slug.length > 0);
}

/**
 * The CURRENT slug for a RETIRED one, or null. NULL for unknown, current,
 * draft, seed, unowned and reserved slugs alike (migration 20260930120000),
 * so a miss is indistinguishable from not-found by design.
 */
export async function resolveRetiredOrganizationSlug(slug: string): Promise<string | null> {
  if (!slug || slug.length > 200) return null;
  const { data, error } = await callRpc("resolve_public_organization_slug", { p_slug: slug });
  if (error) throw error;
  return typeof data === "string" && data.length > 0 ? data : null;
}
